import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useSearch } from "../hooks/useSearch";
import { Input } from "./ui/input";

export function SearchBar({
	workspaceSlug,
}: {
	workspaceSlug: string | undefined;
}) {
	const { query, setQuery, results, loading, error } = useSearch(workspaceSlug);
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	useEffect(() => {
		function handleMouseDown(e: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", handleMouseDown);
		return () => document.removeEventListener("mousedown", handleMouseDown);
	}, []);

	const showDropdown = open && query.length >= 2;

	return (
		<div ref={containerRef} className="relative">
			<Input
				type="search"
				placeholder="Search…"
				value={query}
				onChange={(e) => {
					setQuery(e.target.value);
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				className="w-48 h-8 text-sm"
			/>
			{showDropdown && (
				<div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
					{loading && (
						<p className="px-3 py-2 text-sm text-gray-500">Searching…</p>
					)}
					{error && <p className="px-3 py-2 text-sm text-red-600">{error}</p>}
					{!loading &&
						!error &&
						results.projects.length +
							results.files.length +
							results.members.length ===
							0 && (
							<p className="px-3 py-2 text-sm text-gray-500">No results</p>
						)}
					{!loading && !error && (
						<>
							{results.projects.length > 0 && (
								<section>
									<p className="text-xs font-semibold text-gray-400 uppercase px-3 pt-2 pb-1">
										Projects
									</p>
									{results.projects.map((r) => (
										<button
											key={`project-${r.id}`}
											type="button"
											className="w-full text-left px-3 py-2 hover:bg-gray-100 cursor-pointer"
											onClick={() => {
												setOpen(false);
												setQuery("");
												navigate(`/${workspaceSlug}/projects`);
											}}
										>
											<div className="flex items-center justify-between">
												<span className="text-sm font-medium text-gray-900 truncate">
													{r.name}
												</span>
												{r.status && (
													<span className="text-xs text-gray-500 ml-2 shrink-0">
														{r.status}
													</span>
												)}
											</div>
											{r.description && (
												<p className="text-xs text-gray-500 truncate mt-0.5">
													{r.description}
												</p>
											)}
										</button>
									))}
								</section>
							)}
							{results.files.length > 0 && (
								<section>
									<p className="text-xs font-semibold text-gray-400 uppercase px-3 pt-2 pb-1">
										Files
									</p>
									{results.files.map((r) => (
										<button
											key={`file-${r.id}`}
											type="button"
											className="w-full text-left px-3 py-2 hover:bg-gray-100 cursor-pointer"
											onClick={() => {
												setOpen(false);
												setQuery("");
												navigate(`/${workspaceSlug}/files`);
											}}
										>
											<span className="text-sm font-medium text-gray-900 truncate block">
												{r.name}
											</span>
											<p className="text-xs text-gray-500 truncate mt-0.5">
												{r.contentType}
											</p>
										</button>
									))}
								</section>
							)}
							{results.members.length > 0 && (
								<section>
									<p className="text-xs font-semibold text-gray-400 uppercase px-3 pt-2 pb-1">
										Members
									</p>
									{results.members.map((r) => (
										<button
											key={`member-${r.id}`}
											type="button"
											className="w-full text-left px-3 py-2 hover:bg-gray-100 cursor-pointer"
											onClick={() => {
												setOpen(false);
												setQuery("");
												navigate(`/${workspaceSlug}/settings/team`);
											}}
										>
											<span className="text-sm font-medium text-gray-900 truncate block">
												{r.name}
											</span>
											<p className="text-xs text-gray-500 truncate mt-0.5">
												{r.email}
											</p>
										</button>
									))}
								</section>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
}
